import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { hasDealerContext } from 'utils/hasDealerContext';
import { hasResource } from 'app/components/ReusableUIComponents/HOCs/ResourceProtected/withResourceProtection';
import type { MetricsFrameworkTabId } from '../types';
import { useMetricsFrameworkContext } from '../context/MetricsFrameworkContext';
import {
  buildMetricsFrameworkBreadcrumbs,
  buildMetricsFrameworkPath,
  getAllowedMetricsFrameworkViews,
  getViewFromSearch,
  IMPACT_HUB_USE_MOCK_BUTTON_RESOURCE,
  METRICS_FRAMEWORK_VIEW_CONFIG,
  isMetricsFrameworkTabId,
  resolveMetricsFrameworkBasePath,
} from '../utils/viewConfig';
import { useMetricsFrameworkTimePeriod } from './useMetricsFrameworkTimePeriod';

export const useMetricsFrameworkPage = () => {
  const history = useHistory();
  const location = useLocation();
  const selectedDealer = useSelector(selectDealerSelected);
  const { setDealerContext, setActiveView, mockApiFlag, setMockApiFlag } =
    useMetricsFrameworkContext();
  const {
    timeperiod,
    selectedTimeperiod,
    handleClickChangeTimeperiod,
    renderComponent: timePeriodRenderComponent,
  } = useMetricsFrameworkTimePeriod();

  const canUseMockApiToggle = hasResource(IMPACT_HUB_USE_MOCK_BUTTON_RESOURCE);

  const handleToggleMockData = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canUseMockApiToggle) return;
    setMockApiFlag(event.target.checked);
  };

  useEffect(() => {
    if (!canUseMockApiToggle && mockApiFlag) {
      setMockApiFlag(false);
    }
  }, [canUseMockApiToggle, mockApiFlag, setMockApiFlag]);

  const basePath = useMemo(
    () => resolveMetricsFrameworkBasePath(location.pathname),
    [location.pathname],
  );

  const allowedViews = useMemo(() => getAllowedMetricsFrameworkViews(basePath), [basePath]);

  const requestedView = getViewFromSearch(location.search);
  const normalizedView: MetricsFrameworkTabId | null = isMetricsFrameworkTabId(requestedView)
    ? requestedView
    : null;
  const isViewValid = normalizedView !== null;
  const isViewAllowed = normalizedView ? allowedViews.includes(normalizedView) : false;
  const selectedView: MetricsFrameworkTabId | null = isViewAllowed ? normalizedView : null;

  const dealerObj =
    typeof selectedDealer === 'object' && selectedDealer !== null
      ? (selectedDealer as { site_title?: string; dealer?: string; _id?: string })
      : null;
  const dealerTitle = dealerObj?.site_title ?? null;
  const dealerId = dealerObj?.dealer || dealerObj?._id || null;
  const hasDealer = hasDealerContext(selectedDealer);

  useEffect(() => {
    setDealerContext(dealerId, dealerTitle);
  }, [dealerId, dealerTitle, setDealerContext]);

  useEffect(() => {
    setActiveView(selectedView);
  }, [selectedView, setActiveView]);

  const breadcrumbsList = useMemo(
    () =>
      buildMetricsFrameworkBreadcrumbs({
        dealerTitle,
        basePath,
        selectedView,
      }),
    [basePath, dealerTitle, selectedView],
  );

  const selectedTabIndex = selectedView ? allowedViews.indexOf(selectedView) : 0;

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    const nextView = allowedViews[newIndex];
    if (!nextView) return;
    history.push(buildMetricsFrameworkPath(basePath, nextView));
  };

  const pageTitle = selectedView
    ? `Impact Hub – ${METRICS_FRAMEWORK_VIEW_CONFIG[selectedView].tabLabel}`
    : 'Impact Hub';

  return {
    basePath,
    allowedViews,
    selectedView,
    isViewValid,
    isViewAllowed,
    requestedView,
    breadcrumbsList,
    selectedTabIndex,
    handleTabChange,
    hasDealer,
    dealerId,
    dealerTitle,
    timeperiod,
    selectedTimeperiod,
    handleClickChangeTimeperiod,
    timePeriodRenderComponent,
    pageTitle,
    mockApiFlag,
    handleToggleMockData,
    canUseMockApiToggle,
  };
};
